import { Autocomplete, Chip, TextField } from '@mui/material';
import { SyntheticEvent, useCallback, useMemo } from 'react';
import { MoreChipWithTooltip } from '../../../pages/CompanyProfiles/Summary/components/MoreChipWithTooltip/MoreChipWithTooltip';
import { IFormFieldFreeLabelsMeta } from '../../../view-models/form.view-model';
import { formSelectInlineClassNames } from '../../../theme/component-styles';
import { IBaseFieldProps } from './types';
import { addOptionLabel, useFreeSoloEntry } from './useFreeSoloEntry';

const DefaultMaxTags = 2;

export function FieldFreeLabels(props: IBaseFieldProps<IFormFieldFreeLabelsMeta>) {
  const { formField, formProps } = props;
  const { ref, onChange, onBlur, value } = formProps;
  const { disabled = false, variant, autoFocus = false } = formField ?? {};
  const className = variant === 'form-inline' ? formSelectInlineClassNames : '';
  const options = useMemo(
    () => (formField.rendererMeta?.values ?? []).map((option) => option.value),
    [formField.rendererMeta?.values]
  );
  const labels = useMemo(() => (value as string[] | null | undefined) ?? [], [value]);

  const _onChange = useCallback(
    (_: SyntheticEvent, newValue: string[]) => {
      onChange(newValue);
    },
    [onChange]
  );

  const { commitPending, filterOptions, inputValue, isAddOption, onInputChange } = useFreeSoloEntry<string>({
    enabled: true,
    value: labels,
    options,
    onChange,
  });

  return (
    <Autocomplete
      autoFocus={autoFocus}
      className={className}
      disabled={disabled}
      filterOptions={filterOptions}
      freeSolo
      getLimitTagsText={(more) => {
        const otherLabels = labels.slice(-more).map((label) => {
          return label;
        });
        return (
          <MoreChipWithTooltip
            title={`Other (${more})`}
            moreChipTotal={more}
            otherItems={otherLabels}
          ></MoreChipWithTooltip>
        );
      }}
      inputValue={inputValue}
      limitTags={formField.rendererMeta?.maxTags ?? DefaultMaxTags}
      multiple
      onBlur={() => {
        // Typed text left in the box on click out / tab out becomes a tag rather than being lost.
        commitPending();
        onBlur();
      }}
      onChange={_onChange}
      onInputChange={onInputChange}
      options={options}
      renderInput={(params) => (
        <TextField {...params} ref={ref} placeholder={formField.placeholder} autoFocus={autoFocus} />
      )}
      renderOption={(props, option, state) => (
        <li {...props} key={option}>
          {isAddOption(option, state) ? addOptionLabel(option) : option}
        </li>
      )}
      renderTags={(labels: readonly string[], getTagProps) => {
        return labels.map((label, index) => {
          return <Chip {...getTagProps({ index })} key={label} label={label} size='small' />;
        });
      }}
      value={labels}
    />
  );
}
